<template>
  <div class="login-container">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
    </div>

    <!-- Left Panel - Premium Branding -->
    <div class="branding-panel">
      <div class="branding-content">
        <div class="logo-section">
          <div class="logo-wrapper">
            <div class="era-logo">
              <span class="era-text">ERA</span>
              <span class="era-subtitle">Enterprise Resource Application</span>
            </div>
          </div>
          <h1 class="welcome-title">
            <span class="gradient-text">Bine ați venit</span>
          </h1>
          <p class="welcome-subtitle">Sistem ERP Enterprise de Ultimă Generație</p>
        </div>
        
        <div class="features-grid">
          <div class="feature-card" v-for="(feature, index) in features" :key="index">
            <div class="feature-icon-wrapper">
              <v-icon :color="feature.color" size="28">{{ feature.icon }}</v-icon>
            </div>
            <div class="feature-content">
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-desc">{{ feature.desc }}</p>
            </div>
          </div>
        </div>

        <div class="branding-footer">
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">99.9%</div>
              <div class="stat-label">Uptime</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-value">24/7</div>
              <div class="stat-label">Suport</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-value">ISO</div>
              <div class="stat-label">Certificat</div>
            </div>
          </div>
          <p class="copyright-text">© 2025 Else Digital Solutions S.R.L. • v{{ appVersion }}</p>
        </div>
      </div>
    </div>

    <!-- Right Panel - Premium Login Form -->
    <div class="form-panel">
      <div class="form-content">
        <div class="form-wrapper">
          <!-- Form Header -->
          <div class="form-header">
            <div class="header-badge">
              <v-icon size="20" :color="appColor">mdi-shield-lock</v-icon>
              <span>Autentificare Securizată</span>
            </div>
            <h2 class="form-title">Conectare la Cont</h2>
            <p class="form-description">Introduceți credențialele pentru acces complet</p>
          </div>

          <v-form v-model="form" @submit.prevent="onSubmit" ref="loginForm">
            <!-- Username Field -->
            <div class="input-group">
              <label class="input-label">
                <v-icon size="16" class="mr-1">mdi-account</v-icon>
                Nume Utilizator
              </label>
              <div class="input-wrapper">
                <v-text-field
                  v-model="credentials.username"
                  ref="usernameField"
                  density="comfortable"
                  placeholder="Introduceți numele de utilizator"
                  prepend-inner-icon="mdi-account-outline"
                  variant="outlined"
                  :readonly="loading"
                  :rules="[required]"
                  @keyup.enter="focusPassword"
                  autocomplete="username"
                  class="premium-input"
                  hide-details="auto"
                />
              </div>
            </div>

            <!-- Password Field -->
            <div class="input-group">
              <div class="input-label-row">
                <label class="input-label">
                  <v-icon size="16" class="mr-1">mdi-lock</v-icon>
                  Parolă
                </label>
                <a class="forgot-link" href="/forgot-password" tabindex="-1">
                  <v-icon size="14">mdi-help-circle-outline</v-icon>
                  Ați uitat parola?
                </a>
              </div>
              <div class="input-wrapper">
                <v-text-field
                  v-model="credentials.password"
                  ref="passwordField"
                  :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="visible ? 'text' : 'password'"
                  density="comfortable"
                  placeholder="Introduceți parola"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  @click:append-inner="visible = !visible"
                  :readonly="loading"
                  :rules="[required]"
                  @keyup="checkCapsLock"
                  autocomplete="current-password"
                  class="premium-input"
                  hide-details="auto"
                />
              </div>
              
              <!-- Caps Lock Warning -->
              <transition name="slide-fade">
                <div v-if="capsLockOn" class="capslock-alert">
                  <v-icon size="16" color="warning">mdi-alert</v-icon>
                  <span>Caps Lock este activat</span>
                </div>
              </transition>
            </div>

            <!-- Options Row -->
            <div class="options-row">
              <v-checkbox
                v-model="rememberMe"
                density="compact"
                hide-details
                :color="appColor"
                class="remember-checkbox"
              >
                <template v-slot:label>
                  <span class="checkbox-label">Ține-mă minte</span>
                </template>
              </v-checkbox>
            </div>

            <!-- Security Notice -->
            <div class="security-notice">
              <div class="notice-icon">
                <v-icon size="18" color="info">mdi-information-outline</v-icon>
              </div>
              <div class="notice-text">
                <strong>Notă de securitate:</strong> După 3 încercări eșuate consecutive, contul va fi blocat automat timp de 3 ore.
              </div>
            </div>

            <!-- Submit Button -->
            <v-btn
              block
              size="x-large"
              :color="appColor"
              :disabled="!form"
              type="submit"
              :loading="loading"
              @click="loginStep1"
              class="premium-btn"
              elevation="0"
            >
              <v-icon class="mr-2">mdi-login-variant</v-icon>
              Autentificare
              <v-icon class="ml-2">mdi-arrow-right</v-icon>
            </v-btn>
          </v-form>

          <!-- Footer Links -->
          <div class="form-footer">
            <div class="footer-links">
              <a href="#" class="footer-link">
                <v-icon size="16">mdi-lifebuoy</v-icon>
                <span>Centru Ajutor</span>
              </a>
              <span class="separator">•</span>
              <a href="#" class="footer-link">
                <v-icon size="16">mdi-shield-check</v-icon>
                <span>Securitate</span>
              </a>
              <span class="separator">•</span>
              <a href="#" class="footer-link">
                <v-icon size="16">mdi-file-document-outline</v-icon>
                <span>Termeni</span>
              </a>
            </div>
            <div class="portal-register-link mt-3 text-center">
              <a href="/portal/register" class="footer-link" style="font-size:0.85rem; color:#ff5722; font-weight:600;">
                <v-icon size="16">mdi-account-plus</v-icon>
                <span>Înregistrare Portal HR</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Company Selection Dialog -->
  <v-dialog v-model="showCompanyDialog" persistent max-width="480">
    <v-card class="modern-dialog" elevation="8">
      <v-card-title class="dialog-title-bar">
        <v-icon :color="appColor" size="24" class="mr-2">mdi-domain</v-icon>
        <span>Selectați Compania</span>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-list class="company-selection-list">
          <v-list-item
            v-for="c in companies"
            :key="c.ID"
            @click="selectAndContinue(c)"
            :active="selectedCompanyId === c.ID"
            :color="appColor"
            class="company-option"
          >
            <template #prepend>
              <v-avatar :color="appColor" size="36" class="mr-3">
                <v-icon color="white" size="20">mdi-domain</v-icon>
              </v-avatar>
            </template>

            <v-list-item-title class="company-option-name">
              {{ c.Name }}
            </v-list-item-title>

            <template #append>
              <v-icon
                v-if="selectedCompanyId === c.ID"
                :color="appColor"
                size="20"
              >
                mdi-check-circle
              </v-icon>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          variant="text"
          @click="cancelCompany"
          :disabled="loading"
        >
          <v-icon class="mr-1">mdi-arrow-left</v-icon>
          Înapoi
        </v-btn>
        <v-spacer />
        <v-btn
          :color="appColor"
          variant="elevated"
          :loading="loading"
          :disabled="!selectedCompanyId"
          @click="loginStep2"
        >
          Continuă
          <v-icon class="ml-1">mdi-arrow-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Super Admin Mode Dialog -->
  <v-dialog v-model="showSuperAdminDialog" persistent max-width="450" transition="dialog-bottom-transition">
    <v-card class="acc-card" elevation="24">
      <!-- Header gradient ERA -->
      <div class="acc-header">
        <div class="acc-header-glow"></div>
        <div class="acc-badge">
          <v-icon color="white" size="30">mdi-shield-crown</v-icon>
        </div>
        <div class="acc-header-text">
          <span class="acc-header-title">Mod de Acces</span>
          <span class="acc-header-sub">Alegeți cum doriți să continuați</span>
        </div>
      </div>

      <div class="acc-body">
        <!-- Opțiune principală: Flux Clasic -->
        <button class="acc-primary" @click="goNormal">
          <span class="acc-reco">Recomandat</span>
          <span class="acc-primary-icon"><v-icon color="white" size="30">mdi-briefcase</v-icon></span>
          <span class="acc-primary-text">
            <span class="acc-primary-title">Flux Clasic</span>
            <span class="acc-primary-sub">Utilizare standard a platformei</span>
          </span>
          <v-icon color="white" size="24" class="acc-primary-arrow">mdi-arrow-right</v-icon>
        </button>

        <div class="acc-divider"><span>sau</span></div>

        <!-- Opțiune secundară: Administrare -->
        <button class="acc-secondary" @click="goSuperAdmin">
          <span class="acc-secondary-icon"><v-icon size="20">mdi-cog-outline</v-icon></span>
          <span class="acc-secondary-text">
            <span class="acc-secondary-title">Administrare sistem</span>
            <span class="acc-secondary-sub">Configurare și gestionare sistem</span>
          </span>
          <v-icon size="18" class="acc-secondary-arrow">mdi-chevron-right</v-icon>
        </button>
      </div>
    </v-card>
  </v-dialog>

  <Toaster :snackbar="snackbar" @closeSnackbar="SnackbarClose" />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppSettings from "@/settings/appSettings"
import CompanyServices from '@/services/CompanyService'
import ResponseData from '@/types/responseData'
import Toaster from '../../components/Toaster.vue'
import { AxiosError } from 'axios'
import ParseJwt from '@/services/JwtServices'
import notify from 'devextreme/ui/notify'

export default defineComponent({
  name: 'Login',
  components: { Toaster },
  data() {
    return {
      appColor: AppSettings.APP_Color,
      appVersion: AppSettings.APP_Version,
      visible: false,
      form: false,
      loading: false,
      capsLockOn: false,
      rememberMe: false,

      features: [
        {
          icon: 'mdi-shield-check',
          color: '#4CAF50',
          title: 'Securitate Avansată',
          desc: 'Criptare end-to-end'
        },
        {
          icon: 'mdi-chart-line',
          color: '#2196F3',
          title: 'Analytics Real-Time',
          desc: 'Rapoarte instantanee'
        },
        {
          icon: 'mdi-cloud-sync',
          color: '#9C27B0',
          title: 'Cloud Sync',
          desc: 'Sincronizare automată'
        },
        {
          icon: 'mdi-lightning-bolt',
          color: '#FF9800',
          title: 'Performanță',
          desc: 'Viteză maximă'
        }
      ],

      companies: [] as Array<{ ID: string; Name: string }>,
      selectedCompanyId: null as string | null,

      credentials: { username: '', password: '' },
      ticket: '',

      showCompanyDialog: false,
      showSuperAdminDialog: false,

      snackbar: { visible: false, message: "", code: 0 }
    }
  },
  mounted() {
    // Restaurează credențialele salvate dacă "Ține-mă minte" era bifat
    const saved = localStorage.getItem('ERA_Remember')
    if (saved) {
      try {
        const parsed = JSON.parse(atob(saved))
        this.credentials.username = parsed.username || ''
        this.credentials.password = parsed.password || ''
        this.rememberMe = true
      } catch {
        localStorage.removeItem('ERA_Remember')
      }
    }

    this.$nextTick(() => {
      const field = this.$refs[this.credentials.username ? 'passwordField' : 'usernameField'] as any
      if (field) field.focus()
    })
  },
  methods: {
    focusPassword() {
      const passwordField = this.$refs.passwordField as any
      if (passwordField) {
        passwordField.focus()
      }
    },

    checkCapsLock(event: KeyboardEvent) {
      this.capsLockOn = event.getModifierState && event.getModifierState('CapsLock')
    },

    async loginStep1() {
      if (!this.form) return
      
      this.loading = true
      try {
        const resp: ResponseData = await CompanyServices.login({
          username: this.credentials.username,
          password: this.credentials.password
        })

        const data = resp.data as {
          needCompany: boolean,
          companies: Array<{ ID: string; Name: string }>
        }

        if (data.needCompany) {
          this.companies = data.companies.map(c => ({ ID: String(c.ID), Name: c.Name }))
          localStorage.setItem('ERA_Companies', JSON.stringify(this.companies))
          this.showCompanyDialog = true
        } else {
          const token = (resp.data as any)?.token
          if (token) {
            localStorage.setItem('ERA_Jwt', token)
            this.saveRememberMe()
            const isSuper = ParseJwt.isSuperAdmin()
            this.showCompanyDialog = false
            isSuper ? (this.showSuperAdminDialog = true) : this.$router.go(0)
          }
        }

      } catch (e: any) {
        const msg = this.extractErr(e)
        this.snackbar = { visible: true, message: msg, code: 401 }
      } finally {
        this.loading = false
      }
    },

    async loginStep2() {
      if (!this.selectedCompanyId) return
      this.loading = true
      try {
        const resp: ResponseData = await CompanyServices.login({
          username: this.credentials.username,
          password: this.credentials.password,
          companyID: String(this.selectedCompanyId)
        })

        const { token } = resp.data as { token: string }
        localStorage.setItem('ERA_Jwt', token)
        this.saveRememberMe()

        const selected = this.companies.find(c => c.ID === this.selectedCompanyId)
        if (selected) localStorage.setItem('ERA_Company', JSON.stringify(selected))

        const isSuper = ParseJwt.isSuperAdmin()
        this.showCompanyDialog = false
        isSuper ? (this.showSuperAdminDialog = true) : this.$router.go(0)

      } catch (e: any) {
        const msg = this.extractErr(e)
        this.snackbar = { visible: true, message: msg, code: 401 }
      } finally {
        this.loading = false
      }
    },

    selectAndContinue(company: { ID: string; Name: string }) {
      this.selectedCompanyId = company.ID
      this.loginStep2()
    },
    
    cancelCompany() {
      this.showCompanyDialog = false
      this.selectedCompanyId = null
      this.ticket = ''
      this.companies = []
    },

    goSuperAdmin() {
      this.showSuperAdminDialog = false
      localStorage.setItem("ERA_SuperAdminChoice", "true")
      this.$router.go(0)
    },
    
    goNormal() {
      this.showSuperAdminDialog = false
      localStorage.setItem("ERA_SuperAdminChoice", "false")
      this.$router.go(0)
    },

    saveRememberMe() {
      if (this.rememberMe) {
        localStorage.setItem('ERA_Remember', btoa(JSON.stringify({
          username: this.credentials.username,
          password: this.credentials.password,
        })))
      } else {
        localStorage.removeItem('ERA_Remember')
      }
    },

    SnackbarClose() { this.snackbar.visible = false },
    
    extractErr(e: AxiosError) {
      try {
        const parsed = typeof e.response?.data === 'string'
          ? JSON.parse(e.response.data)
          : e.response?.data
        return parsed?.Message || parsed?.message || 'Eroare la autentificare.'
      } catch { return 'Eroare la autentificare.' }
    },
    
    required(v: any) { return !!v || 'Câmp obligatoriu' },
    
    onSubmit() { /* handled by buttons */ }
  }
})
</script>

<style scoped>
/* ========== GLOBAL CONTAINER ========== */
.login-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: #f5f7fa;
}

/* ========== ANIMATED BACKGROUND ========== */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: float 20s infinite ease-in-out;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%);
  top: -10%;
  left: -10%;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #ff6f00 0%, #ff5722 100%);
  bottom: -10%;
  right: -10%;
  animation-delay: 7s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #ff7043 0%, #ff6f00 100%);
  top: 50%;
  left: 50%;
  animation-delay: 14s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
}

/* ========== BRANDING PANEL ========== */
.branding-panel {
  flex: 1;
  background: linear-gradient(135deg, #f4511e 0%, #ff5722 50%, #ff6f00 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  overflow: hidden;
  z-index: 1;
}

.branding-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  z-index: 0;
}

.branding-panel::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255, 255, 255, 0.02) 10px, rgba(255, 255, 255, 0.02) 20px);
  z-index: 0;
}

.branding-content {
  position: relative;
  z-index: 1;
  color: white;
  max-width: 550px;
  width: 100%;
}

/* Logo Section */
.logo-section {
  text-align: center;
  margin-bottom: 3rem;
}

.logo-wrapper {
  margin-bottom: 2rem;
  animation: fadeInDown 0.8s ease-out;
}

.era-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.era-text {
  font-size: 5rem;
  font-weight: 900;
  letter-spacing: 0.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #ffe0d5 50%, #ffffff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 40px rgba(255, 255, 255, 0.3);
  font-family: 'Arial Black', sans-serif;
}

.era-subtitle {
  font-size: 0.875rem;
  font-weight: 300;
  letter-spacing: 0.3rem;
  color: rgba(255, 255, 255, 0.85);
  text-transform: uppercase;
}

.welcome-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.gradient-text {
  background: linear-gradient(135deg, #ffffff 0%, #ffe0d5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.welcome-subtitle {
  font-size: 1.125rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.5px;
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin: 3rem 0;
}

.feature-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.feature-card:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.feature-icon-wrapper {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: transform 0.3s ease;
}

.feature-card:hover .feature-icon-wrapper {
  transform: scale(1.1) rotate(5deg);
}

.feature-content {
  color: white;
}

.feature-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.feature-desc {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

/* Stats Row */
.stats-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  margin-bottom: 1.5rem;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
}

.branding-footer {
  text-align: center;
}

.copyright-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

/* ========== FORM PANEL ========== */
.form-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
  background: transparent;
}

.form-content {
  width: 100%;
  max-width: 520px;
  position: relative;
}

.form-wrapper {
  background: white;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  position: relative;
  animation: fadeInRight 0.8s ease-out;
}

/* Form Header */
.form-header {
  margin-bottom: 2.5rem;
  text-align: center;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #ffe0d5 0%, #ffcdd2 100%);
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #ff5722;
  margin-bottom: 1.5rem;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #f4511e 0%, #ff6f00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.form-description {
  font-size: 0.95rem;
  color: #718096;
  margin: 0;
}

/* Input Groups */
.input-group {
  margin-bottom: 1.75rem;
}

.input-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.625rem;
}

.input-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.625rem;
}

.forgot-link {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #ff5722;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;
}

.forgot-link:hover {
  color: #f4511e;
  gap: 0.375rem;
}

.input-wrapper {
  position: relative;
}

.premium-input :deep(.v-field) {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.premium-input :deep(.v-field:hover) {
  box-shadow: 0 4px 12px rgba(198, 40, 40, 0.1);
}

.premium-input :deep(.v-field--focused) {
  box-shadow: 0 4px 16px rgba(198, 40, 40, 0.2);
}

/* Caps Lock Alert */
.capslock-alert {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #fff3e0;
  border-left: 3px solid #ff9800;
  border-radius: 8px;
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #e65100;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Options Row */
.options-row {
  margin-bottom: 1.5rem;
}

.remember-checkbox :deep(.v-label) {
  font-size: 0.875rem;
  color: #4a5568;
}

.checkbox-label {
  font-size: 0.875rem;
  color: #4a5568;
}

/* Security Notice */
.security-notice {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 12px;
  margin-bottom: 1.75rem;
  border: 1px solid rgba(25, 118, 210, 0.1);
}

.notice-icon {
  flex-shrink: 0;
}

.notice-text {
  font-size: 0.8rem;
  color: #455a64;
  line-height: 1.5;
}

.notice-text strong {
  color: #ff5722;
  font-weight: 600;
}

/* Premium Button */
.premium-btn {
  height: 56px !important;
  border-radius: 14px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
  overflow: hidden;
}

.premium-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.premium-btn:hover::before {
  left: 100%;
}

.premium-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(198, 40, 40, 0.35) !important;
}

/* Form Footer */
.form-footer {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.footer-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.footer-link {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: #718096;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;
}

.footer-link:hover {
  color: #ff5722;
  gap: 0.5rem;
}

.separator {
  color: #cbd5e0;
  font-size: 0.8rem;
}

/* ========== DIALOGS ========== */
.dialog-card {
  border-radius: 24px !important;
  overflow: visible !important;
  position: relative;
}

.dialog-glow {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #ff5722 0%, #ff6f00 100%);
  border-radius: 24px;
  opacity: 0.5;
  filter: blur(20px);
  z-index: -1;
}

.admin-glow {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 2rem 2rem 1.5rem 2rem !important;
  border-bottom: 1px solid #e2e8f0;
}

.admin-header {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-bottom-color: #ffcc80;
}

.dialog-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #ffe0d5 0%, #ffcdd2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.admin-icon {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
}

.dialog-header-text {
  flex: 1;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.dialog-subtitle {
  font-size: 0.875rem;
  color: #718096;
  margin: 0;
}

.dialog-content {
  padding: 2rem !important;
}

.premium-select :deep(.v-field) {
  border-radius: 12px;
}

.company-item {
  border-radius: 8px;
  margin: 0.25rem 0;
}

.dialog-actions {
  padding: 1.5rem 2rem 2rem 2rem !important;
  border-top: 1px solid #e2e8f0;
}

.action-btn-secondary {
  text-transform: none !important;
  font-weight: 500 !important;
}

.action-btn-primary {
  height: 48px !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

/* Mode Cards */
.mode-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.mode-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(198, 40, 40, 0.05), transparent);
  transition: left 0.5s;
}

.mode-card:hover::before {
  left: 100%;
}

.mode-card:hover {
  border-color: #ff5722;
  transform: translateY(-8px);
  box-shadow: 0 16px 40px rgba(198, 40, 40, 0.2);
}

.mode-icon {
  margin-bottom: 1rem;
}

.mode-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.mode-desc {
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 1rem;
}

.mode-arrow {
  opacity: 0;
  transform: translateX(-10px);
  transition: all 0.3s ease;
}

.mode-card:hover .mode-arrow {
  opacity: 1;
  transform: translateX(0);
}

/* ========== ANIMATIONS ========== */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 960px) {
  .login-container {
    flex-direction: column;
  }

  .branding-panel {
    min-height: 400px;
    padding: 2rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .form-wrapper {
    padding: 2rem;
  }

  .welcome-title {
    font-size: 2.25rem;
  }

  .stats-row {
    gap: 1rem;
  }

  .era-text {
    font-size: 4rem;
  }
}

@media (max-width: 600px) {
  .form-panel {
    padding: 1rem;
  }

  .form-wrapper {
    padding: 1.5rem;
    border-radius: 20px;
  }

  .branding-panel {
    min-height: 350px;
    padding: 1.5rem;
  }

  .welcome-title {
    font-size: 2rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .features-grid {
    gap: 0.75rem;
  }

  .feature-card {
    padding: 1.25rem;
  }

  .stats-row {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .stat-divider {
    display: none;
  }

  .era-text {
    font-size: 3rem;
    letter-spacing: 0.3rem;
  }

  .era-subtitle {
    font-size: 0.7rem;
    letter-spacing: 0.2rem;
  }
}

/* ========== MODERN DIALOGS ========== */
.modern-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-title-bar {
  display: flex;
  align-items: center;
  padding: 1.25rem 1.5rem !important;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  font-weight: 600 !important;
  font-size: 1.125rem !important;
  color: #333 !important;
}

.company-selection-list {
  max-height: 400px;
  overflow-y: auto;
}

.company-option {
  min-height: 64px !important;
  transition: all 0.2s;
  cursor: pointer;
}

.company-option:hover {
  background: rgba(255, 87, 34, 0.05) !important;
}

.company-option-name {
  font-weight: 500;
  font-size: 0.9375rem;
}

.mode-selection-list {
  padding: 0 !important;
}

.mode-option {
  min-height: 80px !important;
  transition: all 0.2s;
  cursor: pointer;
  border-radius: 8px;
  padding: 1rem !important;
}

.mode-option:hover {
  background: rgba(255, 87, 34, 0.05) !important;
}

.mode-option-title {
  font-weight: 600;
  font-size: 1rem;
  color: #333;
}

.mode-option-subtitle {
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.25rem;
}

/* ===== Mod de Acces (redesign) ===== */
.acc-card {
  border-radius: 22px !important;
  overflow: hidden;
  background: #ffffff;
}

.acc-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 26px 26px 24px;
  background: linear-gradient(135deg, #ff2c21 0%, #ff6a4a 100%);
  color: #fff;
  overflow: hidden;
}
.acc-header-glow {
  position: absolute;
  top: -60px; right: -40px;
  width: 180px; height: 180px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.35) 0%, transparent 70%);
  pointer-events: none;
}
.acc-badge {
  display: flex; align-items: center; justify-content: center;
  width: 54px; height: 54px; flex-shrink: 0;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.10);
  backdrop-filter: blur(4px);
}
.acc-header-text { display: flex; flex-direction: column; line-height: 1.25; z-index: 1; }
.acc-header-title { font-size: 20px; font-weight: 800; letter-spacing: 0.2px; }
.acc-header-sub { font-size: 12.5px; color: rgba(255, 255, 255, 0.9); }

.acc-body { padding: 24px; display: flex; flex-direction: column; gap: 6px; }

/* Opțiune principală */
.acc-primary {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: left;
  border: none;
  cursor: pointer;
  padding: 20px 22px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ff2c21 0%, #ff6a4a 100%);
  box-shadow: 0 8px 22px rgba(255, 44, 33, 0.38);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.acc-primary::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.25) 50%, transparent 70%);
  transform: translateX(-120%);
  transition: transform 0.6s ease;
}
.acc-primary:hover { transform: translateY(-3px); box-shadow: 0 14px 30px rgba(255, 44, 33, 0.48); }
.acc-primary:hover::after { transform: translateX(120%); }
.acc-primary-icon {
  display: flex; align-items: center; justify-content: center;
  width: 54px; height: 54px; flex-shrink: 0;
  background: rgba(255, 255, 255, 0.20);
  border-radius: 14px;
  z-index: 1;
}
.acc-primary-text { display: flex; flex-direction: column; flex: 1; z-index: 1; }
.acc-primary-title { font-size: 18px; font-weight: 800; color: #fff; }
.acc-primary-sub { font-size: 12.5px; color: rgba(255, 255, 255, 0.92); margin-top: 3px; }
.acc-primary-arrow { transition: transform 0.18s ease; z-index: 1; }
.acc-primary:hover .acc-primary-arrow { transform: translateX(5px); }

/* Pastilă Recomandat */
.acc-reco {
  position: absolute;
  top: 10px; right: 12px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #ff2c21;
  background: #fff;
  padding: 3px 9px;
  border-radius: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

/* Divider "sau" */
.acc-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #cbd5e1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 8px 0;
}
.acc-divider::before, .acc-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #edf0f5;
}
.acc-divider span { padding: 0 12px; }

/* Opțiune secundară (discretă) */
.acc-secondary {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  cursor: pointer;
  padding: 14px 18px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #64748b;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s;
}
.acc-secondary:hover { background: #f8f9fc; color: #1e293b; border-color: #cbd5e1; transform: translateY(-1px); }
.acc-secondary-icon {
  display: flex; align-items: center; justify-content: center;
  width: 42px; height: 42px; flex-shrink: 0;
  border-radius: 12px;
  background: #f1f5f9;
  color: #64748b;
  transition: background 0.15s, color 0.15s;
}
.acc-secondary:hover .acc-secondary-icon { background: #e8edf3; color: #475569; }
.acc-secondary-text { display: flex; flex-direction: column; flex: 1; text-align: left; }
.acc-secondary-title { font-size: 14px; font-weight: 700; }
.acc-secondary-sub { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.acc-secondary-arrow { color: #94a3b8; transition: transform 0.15s; }
.acc-secondary:hover .acc-secondary-arrow { transform: translateX(3px); }

</style>
